import { useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import type { MarketingActivity, ActivityStatus } from '@/types';
import { cn, formatCurrency, formatDate, statusColors, priorityColors, getInitials } from '@/lib/utils';
import { ACTIVITY_STATUS_SWATCH, TAG_TINT } from '@/lib/tag-palette';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';
import { toast } from 'sonner';
import {
  Search,
  Linkedin,
  Newspaper,
  Mail,
  Megaphone,
  Share2,
  Calendar,
  FileText,
  Globe,
  Video,
  MoreHorizontal,
} from 'lucide-react';

const COLUMNS: { id: ActivityStatus; label: string; headerClass: string }[] = [
  { id: 'backlog', label: 'Backlog', headerClass: TAG_TINT[ACTIVITY_STATUS_SWATCH.backlog] },
  { id: 'todo', label: 'To-Do', headerClass: TAG_TINT[ACTIVITY_STATUS_SWATCH.todo] },
  { id: 'in_progress', label: 'In Bearbeitung', headerClass: TAG_TINT[ACTIVITY_STATUS_SWATCH.in_progress] },
  { id: 'review', label: 'Review', headerClass: TAG_TINT[ACTIVITY_STATUS_SWATCH.review] },
  { id: 'done', label: 'Erledigt', headerClass: TAG_TINT[ACTIVITY_STATUS_SWATCH.done] },
];

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  google_ads: Search,
  linkedin_ads: Linkedin,
  print: Newspaper,
  email: Mail,
  campaign: Megaphone,
  social: Share2,
  event: Calendar,
  content: FileText,
  seo: Globe,
  webinar: Video,
  other: MoreHorizontal,
};

interface KanbanViewProps {
  activities: MarketingActivity[];
  loading: boolean;
  onRefresh: () => void;
  onActivityClick: (id: string) => void;
}

export default function KanbanView({
  activities,
  loading,
  onRefresh,
  onActivityClick,
}: KanbanViewProps) {
  const activitiesByStatus = useMemo(() => {
    const map: Record<ActivityStatus, MarketingActivity[]> = {
      backlog: [],
      todo: [],
      in_progress: [],
      review: [],
      done: [],
    };
    for (const a of activities) {
      if (map[a.status]) map[a.status].push(a);
    }
    return map;
  }, [activities]);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      if (result.destination.droppableId === result.source.droppableId) return;
      const newStatus = result.destination.droppableId as ActivityStatus;
      const activityId = result.draggableId;
      try {
        await api.patch<ApiResponse<MarketingActivity>>(`/activities/${activityId}`, {
          status: newStatus,
        });
        onRefresh();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Status konnte nicht geändert werden.');
      }
    },
    [onRefresh]
  );

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => (
          <div
            key={col.id}
            className={cn('w-72 shrink-0 rounded-lg border border-border', col.headerClass)}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="font-medium">{col.label}</span>
              <span className="text-sm text-muted">0</span>
            </div>
            <div className="space-y-2 p-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-lg bg-card-hover/50"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => (
          <Droppable key={col.id} droppableId={col.id}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={cn(
                  'w-72 shrink-0 rounded-lg border border-border',
                  col.headerClass,
                  snapshot.isDraggingOver && 'ring-2 ring-border/50'
                )}
              >
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <span className="font-medium">{col.label}</span>
                  <Badge variant="secondary" className="text-xs">
                    {activitiesByStatus[col.id].length}
                  </Badge>
                </div>
                <div className="min-h-[200px] space-y-2 p-2">
                  {activitiesByStatus[col.id].map((activity, index) => (
                    <Draggable
                      key={activity.id}
                      draggableId={activity.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={cn(
                            'cursor-grab active:cursor-grabbing',
                            snapshot.isDragging && 'shadow-lg ring-2 ring-border'
                          )}
                          onClick={() => onActivityClick(activity.id)}
                        >
                          <CardContent className="p-3">
                            <p className="mb-2 line-clamp-2 text-sm font-medium">
                              {activity.title}
                            </p>
                            <div className="mb-2 flex flex-wrap gap-1">
                              {(() => {
                                const Icon = TYPE_ICONS[activity.type] ?? MoreHorizontal;
                                return (
                                  <span
                                    className="inline-flex items-center rounded bg-card-hover px-1.5 py-0.5 text-xs"
                                    title={activity.type}
                                  >
                                    <Icon className="mr-1 h-3 w-3" />
                                    {activity.type}
                                  </span>
                                );
                              })()}
                              <Badge
                                className={cn(
                                  'text-xs',
                                  priorityColors[activity.priority]
                                )}
                              >
                                {activity.priority}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted">
                              {activity.assignee ? (
                                <Avatar className="h-6 w-6">
                                  <AvatarFallback className="text-[10px]">
                                    {getInitials(activity.assignee.full_name)}
                                  </AvatarFallback>
                                </Avatar>
                              ) : (
                                <span>—</span>
                              )}
                              {activity.end_date && (
                                <span>{formatDate(activity.end_date)}</span>
                              )}
                            </div>
                            {activity.budget_planned > 0 && (
                              <p className="mt-1 text-xs text-muted">
                                {formatCurrency(activity.budget_planned)}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
}
